import { useState, useEffect } from "react";
import {
  ChemicalProduct,
  ChemicalProductCreateRequest,
  ChemicalProductUpdateRequest,
  productApi,
} from "../../services/poolService";
import { usePermissions } from "../../hooks/usePermissions";
import { toast } from "react-hot-toast";

interface ChemicalProductFormData extends ChemicalProductCreateRequest {
  id?: number;
}

function getChemicalSaveErrorMessage(
  error: unknown,
  isUpdate: boolean,
): string {
  const fallback = `Error al ${isUpdate ? "actualizar" : "crear"} el producto químico`;
  if (!(error instanceof Error)) return fallback;

  const typedError = error as Error & { status?: number; data?: any };
  const backendMessage =
    typeof typedError.data?.message === "string" ? typedError.data.message : "";

  const hayDuplicado = [
    typedError.message,
    backendMessage,
    JSON.stringify(typedError.data ?? {}),
  ]
    .join(" ")
    .toLowerCase()
    .includes("duplicate");

  if (typedError.status === 409 || hayDuplicado) {
    return "El código del producto ya existe. Usa un identificador diferente.";
  }

  if (
    typedError.status === 500 &&
    backendMessage.toLowerCase().includes("unexpected error")
  ) {
    return "No se pudo guardar el producto. Verifica que el código no esté repetido.";
  }

  return typedError.message || fallback;
}

const ChemicalsContent = () => {
  const [products, setProducts] = useState<ChemicalProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<ChemicalProduct | null>(
    null,
  );
  const [formData, setFormData] = useState<ChemicalProductFormData>({
    productId: "",
    name: "",
    unit: "",
    icon: "",
  });

  const { canReadChemicals, canWriteChemicals, canDeleteChemicals } =
    usePermissions();

  const normalizedProductId = formData.productId.trim().toUpperCase();
  const isDuplicateProductId =
    normalizedProductId.length > 0 &&
    products.some(
      (p) =>
        p.productId.trim().toUpperCase() === normalizedProductId &&
        p.id !== editingProduct?.id,
    );

  useEffect(() => {
    if (canReadChemicals) {
      loadProducts();
    }
  }, [canReadChemicals]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const data = await productApi.getAll();
      setProducts(data);
    } catch (error) {
      toast.error("Error al cargar productos químicos");
      console.error("Error loading products:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingProduct(null);
    setFormData({
      productId: "",
      name: "",
      unit: "",
      icon: "",
    });
    setIsModalOpen(true);
  };

  const handleEdit = (product: ChemicalProduct) => {
    setEditingProduct(product);
    setFormData({
      id: product.id,
      productId: product.productId,
      name: product.name,
      unit: product.unit,
      icon: product.icon || "",
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id: number, name: string) => {
    if (!canDeleteChemicals) {
      toast.error("No tienes permisos para eliminar productos químicos");
      return;
    }

    if (window.confirm(`¿Estás seguro de que deseas eliminar "${name}"?`)) {
      try {
        await productApi.delete(id);
        toast.success("Producto químico eliminado exitosamente");
        loadProducts();
      } catch (error) {
        toast.error("Error al eliminar el producto químico");
        console.error("Error deleting product:", error);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!canWriteChemicals) {
      toast.error("No tienes permisos para gestionar productos químicos");
      return;
    }

    if (isDuplicateProductId) {
      toast.error(
        `Ya existe un producto con el código ${normalizedProductId}. Usa otro código.`,
      );
      return;
    }

    try {
      if (editingProduct) {
        // Update existing product
        const updateData: ChemicalProductUpdateRequest = {
          productId: normalizedProductId,
          name: formData.name,
          unit: formData.unit,
          icon: formData.icon,
        };
        await productApi.update(editingProduct.id, updateData);
        toast.success("Producto químico actualizado exitosamente");
      } else {
        // Create new product
        const createData: ChemicalProductCreateRequest = {
          productId: normalizedProductId,
          name: formData.name,
          unit: formData.unit,
          icon: formData.icon,
        };
        await productApi.create(createData);
        toast.success("Producto químico creado exitosamente");
      }

      setIsModalOpen(false);
      loadProducts();
    } catch (error) {
      toast.error(getChemicalSaveErrorMessage(error, Boolean(editingProduct)));
      console.error("Error saving product:", error);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    const normalizedValue =
      name === "productId" ? value.toUpperCase().replace(/\s+/g, "") : value;
    setFormData((prev) => ({
      ...prev,
      [name]: normalizedValue,
    }));
  };

  if (!canReadChemicals) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Acceso Denegado
          </h1>
          <p className="text-gray-600">
            No tienes permisos para ver esta página.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">
              Gestión de Productos Químicos
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              Administra los productos químicos disponibles para el
              mantenimiento de piscinas
            </p>
          </div>

          {/* Actions */}
          {canWriteChemicals && (
            <div className="mb-6">
              <button
                onClick={handleCreate}
                className="bg-teal-600 text-white px-6 py-2 rounded-md hover:bg-teal-700 transition-colors"
              >
                ➕ Nuevo Producto Químico
              </button>
            </div>
          )}

          {/* Products Table */}
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">
                  Cargando productos químicos...
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Código
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Nombre
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Unidad
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Estado
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fecha Creación
                      </th>
                      {(canWriteChemicals || canDeleteChemicals) && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Acciones
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {products.map((product) => (
                      <tr key={product.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {product.productId}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {product.icon && (
                              <img
                                className="h-8 w-8 rounded-full mr-3"
                                src={`/icons/${product.icon}`}
                                alt={product.name}
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                }}
                              />
                            )}
                            <div className="text-sm font-medium text-gray-900">
                              {product.name}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {product.unit}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              product.active
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {product.active ? "Activo" : "Inactivo"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {product.createdAt
                            ? new Date(product.createdAt).toLocaleDateString(
                                "es-ES",
                              )
                            : "N/A"}
                        </td>
                        {(canWriteChemicals || canDeleteChemicals) && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              {canWriteChemicals && (
                                <button
                                  onClick={() => handleEdit(product)}
                                  className="text-teal-600 hover:text-teal-900"
                                >
                                  ✏️ Editar
                                </button>
                              )}
                              {canDeleteChemicals && (
                                <button
                                  onClick={() =>
                                    handleDelete(product.id, product.name)
                                  }
                                  className="text-red-600 hover:text-red-900"
                                >
                                  🗑️ Eliminar
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                    {products.length === 0 && (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-6 py-8 text-center text-gray-500"
                        >
                          No hay productos químicos registrados
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal for Create/Edit */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingProduct
                  ? "Editar Producto Químico"
                  : "Nuevo Producto Químico"}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Código del Producto
                  </label>
                  <input
                    type="text"
                    name="productId"
                    value={formData.productId}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                    required
                    placeholder="ej: CLG001"
                  />
                  {isDuplicateProductId && (
                    <p className="mt-1 text-xs text-red-600">
                      Ya existe un producto con ese código.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Nombre del Producto
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                    required
                    placeholder="ej: Cloro Granulado"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Unidad de Medida
                  </label>
                  <select
                    name="unit"
                    value={formData.unit}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                    required
                  >
                    <option value="">Seleccionar unidad</option>
                    <option value="kg">Kilogramos (kg)</option>
                    <option value="L">Litros (L)</option>
                    <option value="ml">Mililitros (ml)</option>
                    <option value="g">Gramos (g)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Icono (opcional)
                  </label>
                  <input
                    type="text"
                    name="icon"
                    value={formData.icon}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                    placeholder="ej: chlorine.png"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isDuplicateProductId}
                    className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {editingProduct ? "Actualizar" : "Crear"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChemicalsContent;
